import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@material-ui/core';
import { Agent, MerchantStructure } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Font } from '@/components/shared/components/Font';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { AgentsForm } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Agents/AgentsForm';
import { AgentsSelectForm } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Agents/AgentsSelectForm';
import { NewAgent } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Agents/types/NewAgent';
import { Configuration } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Configuration';
import { Processing } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Processing/Processing';
import { SelectCard } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/SelectCard';
import { useCloneOrUpdateAgents } from '@/components/widgets/AgentDetailActions/components/CloneAgent/hooks/useCloneOrUpdateAgents';
import {
  CloneAgentFormFields,
  CloneAgentFormSchema,
} from '@/components/widgets/AgentDetailActions/components/CloneAgent/schemas/CloneAgentForm.schema';
import { CloneType } from '@/components/widgets/AgentDetailActions/components/CloneAgent/types/CloneType';
import { Step } from '@/components/widgets/AgentDetailActions/components/CloneAgent/types/Step';
import { useGetMerchantStructureById } from '@/hooks/requests/merchant/useGetMerchantStructureById';

export interface FormValues {
  clonedAgentId: string;
  cloneConfiguration: boolean;
  cloneAgentInLenderContext: boolean;
  clonePricings: boolean;
  cloneCreateUser: boolean;
  sendPasswordRecovery: boolean;
}

interface CloneAgentProps {
  type: CloneType;
  clonedAgent: Agent;
  merchantStructure: MerchantStructure;
  opened: boolean;
  setOpened: (boolean) => void;
  handleClose;
}

export const CloneAgent = ({
  type,
  clonedAgent,
  merchantStructure,
  opened,
  setOpened,
  handleClose,
}: CloneAgentProps) => {
  const { onSubmit, agentsCloned, isSubmitting } = useCloneOrUpdateAgents({
    clonedAgent,
    merchantId: merchantStructure.merchant.id,
  });
  const { revalidate } = useGetMerchantStructureById(merchantStructure.merchant.id);
  const { getTextIdTranslation: t } = useTranslation();

  const [newAgents, setNewAgents] = useState<NewAgent[]>([]);

  const defaultValues: FormValues = {
    clonedAgentId: '',
    cloneConfiguration: true,
    cloneAgentInLenderContext: true,
    clonePricings: true,
    cloneCreateUser: false,
    sendPasswordRecovery: false,
  };

  const formMethods = useForm<CloneAgentFormFields>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(CloneAgentFormSchema(t, newAgents)),
    defaultValues: {
      ...defaultValues,
      clonePricings: !!(clonedAgent?.pricingIds && clonedAgent?.pricingIds.length > 0),
      clonedAgentId: clonedAgent.id,
    },
  });

  if (!formMethods) return null;

  const { handleSubmit } = formMethods;

  const [step, setStep] = useState<Step>(Step.AGENTS);

  useEffect(() => {
    if (isSubmitting && step !== Step.PROCESSING) {
      setStep(Step.PROCESSING);
    }
  }, [agentsCloned, isSubmitting]);

  const onClose = async () => {
    await revalidate();
    formMethods?.reset(defaultValues);
    setStep(Step.AGENTS);
    handleClose();
  };

  const onPrev = () => setStep(Step.AGENTS);

  const addAgent = (agent: NewAgent): void => {
    setNewAgents((s) => [...s, agent]);
  };

  const removeAgent = (agent: NewAgent): void => {
    const filteredAgents = newAgents.filter((newAgent) => newAgent.agentId !== agent.agentId);
    setNewAgents([...filteredAgents]);
  };

  return (
    <FormProvider {...formMethods}>
      <NemuruDialog
        avoidDefaultClose
        opened={opened}
        setOpened={setOpened}
        buttons={
          <>
            {step === Step.AGENTS && (
              <>
                <StandardButton onClick={onClose}>
                  <Text tId={'CANCEL'} />
                </StandardButton>
                <StandardButton main disabled={!newAgents.length} onClick={() => setStep(Step.CONFIGURATION)}>
                  <Text tId={'CONTINUE'} />
                </StandardButton>
              </>
            )}
            {step === Step.CONFIGURATION && (
              <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} width={'100%'}>
                <StandardButton
                  onClick={onPrev}
                  disabled={isSubmitting}
                  startIcon={<i className="material-icons-outlined">arrow_back</i>}
                >
                  <Text tId={'BACK'} />
                </StandardButton>
                <Box display={'flex'} gridGap={'16px'}>
                  <StandardButton onClick={onClose} disabled={isSubmitting}>
                    <Text tId={'CANCEL'} />
                  </StandardButton>
                  <StandardButton
                    main
                    onClick={handleSubmit((data) => onSubmit(data as FormValues, newAgents))}
                    loading={isSubmitting}
                  >
                    {newAgents.length === 1 ? (
                      <Text tId={type === CloneType.CLONE ? 'CREATE_AGENT' : 'UPDATE_AGENT'} />
                    ) : (
                      <Text
                        tId={type === CloneType.CLONE ? 'CREATE_NEW_AGENTS_NUMBER' : 'UPDATE_NEW_AGENTS_NUMBER'}
                        dynamicDataTexts={[newAgents.length.toFixed(0)]}
                      />
                    )}
                  </StandardButton>
                </Box>
              </Box>
            )}
            {step === Step.PROCESSING && (
              <StandardButton main onClick={onClose} loading={isSubmitting}>
                <Text tId={'CLOSE'} />
              </StandardButton>
            )}
          </>
        }
      >
        <Font variant={'h3'} color={'title'}>
          <Text tId={type === CloneType.CLONE ? 'CREATE_NEW_AGENTS' : 'MODIFY_AGENTS'} />
        </Font>
        <Box mt={3} />
        <Font variant={'body2'} color={'textSecondary'}>
          <Text tId={'FROM_THIS_AGENT'} />
        </Font>
        <Box mt={2} />
        <SelectCard
          disabled
          name={'clonedAgentId'}
          label={'AGENT_TO_CLONE'}
          iconName={'account_circle'}
          selectOptions={[{ value: clonedAgent.id, display: clonedAgent.name }]}
        />
        <Box mt={3} />
        {step === Step.AGENTS && (
          <>
            {type === CloneType.CLONE && (
              <AgentsForm
                agents={newAgents}
                merchantStructure={merchantStructure}
                addAgent={addAgent}
                removeAgent={removeAgent}
              />
            )}
            {type === CloneType.UPDATE && (
              <AgentsSelectForm
                agents={newAgents}
                merchantStructure={merchantStructure}
                clonedAgentId={clonedAgent.id}
                addAgent={addAgent}
                removeAgent={removeAgent}
              />
            )}
          </>
        )}
        {step === Step.CONFIGURATION && <Configuration type={type} clonedAgent={clonedAgent} />}
        {step === Step.PROCESSING && (
          <Processing isSubmitting={isSubmitting} agents={agentsCloned} merchantStructure={merchantStructure} />
        )}
      </NemuruDialog>
    </FormProvider>
  );
};
